import { useEffect, useState } from "react";
import apiService from "src/services/ApiCallService";
import { TestedPartyMapping } from "src/models/tested-party-mapping/TestedPartyMapping";
import ErrorUtils from "src/utils/ErrorUtils";
import { TESTED_PARTY } from "src/components/tested-party-mapping/Constants";

/**
 * Hook to fetch tested party CLI mappings via getAllCliReportingConfig(CONFIG_TYPE_TESTED_PARTY).
 * Follows existing service hook patterns (useState + useEffect, React 16 compatible).
 *
 * @param shouldFetch - When true, triggers the API call. Only fetches when transitioning to true.
 * @returns [data, loading, error] tuple
 */
export function useTestedPartyMappings(shouldFetch: boolean): [TestedPartyMapping[], boolean, string] {
    const [result, setResult] = useState<TestedPartyMapping[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!shouldFetch) {
            return;
        }

        let cancelled = false;

        async function fetchData() {
            try {
                setLoading(true);
                setError('');
                const response = await apiService.getAllCliReportingConfig(TESTED_PARTY);
                if (!cancelled) {
                    const records: TestedPartyMapping[] = response?.CliReportingConfigs ?? [];
                    setResult(records);
                }
            } catch (ex) {
                if (!cancelled) {
                    setError(ErrorUtils.getMessage(ex));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [shouldFetch]);

    return [result, loading, error];
}
